import { faList } from '@fortawesome/pro-solid-svg-icons/faList'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { faSitemap } from '@fortawesome/pro-solid-svg-icons/faSitemap'
import { faServer } from '@fortawesome/pro-solid-svg-icons/faServer'
import fetch from 'src/utils/fetch'
import {
    buildApiFilters,
    buildQueryString,
    mapAnyValueToEmpty,
} from 'src/utils/queryUtils'

export const ITEMS_PER_PAGE = 10
export const FILTER_ANY_VALUE = '__ANY__'
export const FILTER_ANY_LABEL = 'Any'
export const ORGANIZATION_SCOPE_ALL_VALUE = 'all'
export const ORGANIZATION_SCOPE_ALL_LABEL = 'All'
export const TEMPLATE_TYPE_FILTER_KEY = 'templateType'
export const SWAP_PRIORITY_UP = 'up'
export const SWAP_PRIORITY_DOWN = 'down'

export const INITIAL_FILTERS = Object.freeze({
    templateType: FILTER_ANY_VALUE,
    organization: FILTER_ANY_VALUE,
    runbookCategory: FILTER_ANY_VALUE,
    deviceType: FILTER_ANY_VALUE,
})

export const FILTER_FIELDS = Object.freeze([
    { key: TEMPLATE_TYPE_FILTER_KEY, label: 'Template type', icon: faList },
    { key: 'organization', label: 'Organization', icon: faUser },
    { key: 'runbookCategory', label: 'Runbook category', icon: faSitemap },
    { key: 'deviceType', label: 'Device type', icon: faServer },
])

const ORGANIZATION_SCOPE_VALUES = Object.freeze([ORGANIZATION_SCOPE_ALL_VALUE])
const ORGANIZATION_ID_PATTERN = /^\d+$/

/**
 * Converts organization scope value to a user-friendly label.
 * @param {string} scopeValue
 * @returns {string}
 */
export const getOrganizationScopeLabel = (scopeValue) =>
    String(scopeValue).toLowerCase() === ORGANIZATION_SCOPE_ALL_VALUE
        ? ORGANIZATION_SCOPE_ALL_LABEL
        : String(scopeValue)

/**
 * Builds organization filter options from template records.
 * Includes: Any option, then All (if present), then remaining options sorted by label.
 * @param {Array<object>} templates
 * @param {string} anyValue
 * @returns {Array<{value: string, label: string}>}
 */
export const buildOrganizationFilterOptions = (
    templates,
    anyValue = FILTER_ANY_VALUE
) => {
    const organizationEntriesByValue = new Map()

    for (const template of templates) {
        if (
            template.organization !== null &&
            template.organization !== undefined
        ) {
            const organizationValue = String(template.organization)
            const organizationLabel =
                template.organization_name || organizationValue

            organizationEntriesByValue.set(organizationValue, {
                value: organizationValue,
                label: organizationLabel,
            })
        }

        if (template.organization_scope) {
            const organizationScope = String(template.organization_scope)

            organizationEntriesByValue.set(organizationScope, {
                value: organizationScope,
                label: getOrganizationScopeLabel(organizationScope),
            })
        }
    }

    const organizationEntries = Array.from(organizationEntriesByValue.values())
    const allOrganizationEntry = organizationEntries.find(
        (entry) =>
            String(entry.value).toLowerCase() === ORGANIZATION_SCOPE_ALL_VALUE
    )
    const sortedOrganizationEntries = organizationEntries
        .filter(
            (entry) =>
                String(entry.value).toLowerCase() !==
                ORGANIZATION_SCOPE_ALL_VALUE
        )
        .sort((a, b) =>
            String(a.label).localeCompare(String(b.label), undefined, {
                sensitivity: 'base',
                numeric: true,
            })
        )

    return [
        { value: anyValue, label: FILTER_ANY_LABEL },
        ...(allOrganizationEntry ? [allOrganizationEntry] : []),
        ...sortedOrganizationEntries,
    ]
}

/**
 * Returns a sortable value for a given row and key.
 * @param {object} row - The data row.
 * @param {string} key - The key to sort by.
 * @returns {number|string}
 */
export const getSortValue = (row, key) => {
    if (key === 'createdAt' || key === 'lastUpdated') {
        return new Date(row[key]).getTime()
    }

    if (key === 'priority' || key === 'id') {
        return Number(row[key])
    }

    return row[key]
}

/**
 * Compares two values for sorting, supporting numbers and strings.
 * @param {number|string} a
 * @param {number|string} b
 * @param {number} sortDirection
 * @returns {number}
 */
export const compareByType = (a, b, sortDirection) => {
    if (typeof a === 'number' && typeof b === 'number') {
        return (a - b) * sortDirection
    }

    return (
        String(a).localeCompare(String(b), undefined, {
            sensitivity: 'base',
            numeric: true,
        }) * sortDirection
    )
}

/**
 * Maps database template type values to display labels.
 * @param {string} value - The database value.
 * @returns {string} The display label.
 */
export const getTemplateTypeLabel = (value) => {
    const labelMap = {
        runbook_framework: 'Runbook Framework',
        action_group: 'Action Group',
    }
    return labelMap[value] || value
}

/**
 * Maps database runbook category values to display labels.
 * @param {*} value - The database value.
 * @returns {string} The display label.
 */
export const getRunbookCategoryLabel = (value) => {
    const labelMap = {
        all: ORGANIZATION_SCOPE_ALL_LABEL,
        cybersecurity: 'Cybersecurity',
        failover: 'Failover',
        restart: 'Restart',
    }

    if (!value) {
        return ORGANIZATION_SCOPE_ALL_LABEL
    }

    return labelMap[String(value).toLowerCase()] || String(value)
}
/**
 * Maps database device type values to display labels.
 * @param {*} value - The database value.
 * @returns {string} The display label.
 */
export const getDeviceTypeLabel = (value) => {
    const labelMap = {
        all: ORGANIZATION_SCOPE_ALL_LABEL,
        server: 'Server',
        network: 'Network',
        storage: 'Storage',
        wp: 'WP',
    }

    if (!value) {
        return ORGANIZATION_SCOPE_ALL_LABEL
    }

    return labelMap[String(value).toLowerCase()] || String(value)
}

/**
 * Returns the display label for an organization based on its scope and ID.
 * @param {*} scopeValue - The scope value of the organization.
 * @param {*} organizationName - The name of the organization.
 * @param {*} organizationId - The ID of the organization.
 * @returns {string} The display label for the organization.
 */
export const getOrganizationLabel = (scopeValue, organizationName, organizationId) => {
    if (
        String(scopeValue).toLowerCase() === ORGANIZATION_SCOPE_ALL_VALUE ||
        !organizationId
    ) {
        return ORGANIZATION_SCOPE_ALL_LABEL
    }

    return organizationName || String(organizationId)
}

/**
 * Converts UI filter selections into API-ready query values.
 * @param {{
 *  templateType: string,
 *  organization: string,
 *  runbookCategory: string,
 *  deviceType: string
 * }} filters - Filters selected in the templates UI.
 * @returns {{
 *  template_type: string,
 *  organization: string,
 *  organization_scope: string,
 *  runbook_category: string,
 *  device_type: string
 * }} API filter payload where "any" selections are converted to empty values.
 */
export const buildRunbookTemplatesApiFilters = (filters) => {
    const normalizedOrganization = String(filters.organization)
    const isAnyOrganization = filters.organization === FILTER_ANY_VALUE
    const normalizedOrganizationScope = normalizedOrganization.toLowerCase()

    return buildApiFilters(filters, {
        template_type: (currentFilters) =>
            mapAnyValueToEmpty(
                currentFilters[TEMPLATE_TYPE_FILTER_KEY],
                FILTER_ANY_VALUE
            ),
        organization: () =>
            !isAnyOrganization &&
            ORGANIZATION_ID_PATTERN.test(normalizedOrganization)
                ? normalizedOrganization
                : '',
        organization_scope: () =>
            !isAnyOrganization &&
            ORGANIZATION_SCOPE_VALUES.includes(normalizedOrganizationScope)
                ? normalizedOrganizationScope
                : '',
        runbook_category: (currentFilters) =>
            mapAnyValueToEmpty(
                currentFilters.runbookCategory,
                FILTER_ANY_VALUE
            ),
        device_type: (currentFilters) =>
            mapAnyValueToEmpty(currentFilters.deviceType, FILTER_ANY_VALUE),
    })
}

/**
 * Determines whether a templates fetch should be skipped to avoid duplicate requests.
 * @param {{
 *  isLoading: boolean,
 *  loadedRequestKey: string,
 *  currentRequestKey: string,
 *  requestedRequestKey: string,
 *  loadedPage: number|null,
 *  currentPage: number,
 *  loadedSort: string,
 *  currentSort: string,
 *  requestedPage: number|null,
 *  requestedSort: string
 * }} params - Request/loading snapshot used to prevent redundant API calls.
 * @returns {boolean} True when the request is already loaded, in-flight, or loading.
 */
export const shouldSkipRunbookTemplatesRequest = ({
    isLoading,
    loadedRequestKey,
    currentRequestKey,
    requestedRequestKey,
    loadedPage,
    currentPage,
    loadedSort,
    currentSort,
    requestedPage,
    requestedSort,
}) =>
    isLoading ||
    loadedRequestKey === currentRequestKey ||
    requestedRequestKey === currentRequestKey ||
    (loadedPage === currentPage &&
        loadedSort === currentSort &&
        loadedRequestKey === '') ||
    (requestedPage === currentPage &&
        requestedSort === currentSort &&
        requestedRequestKey === '')

/**
 * Builds API sort query value from UI sort state.
 * @param {string|null} sortBy - Field currently used for sorting.
 * @param {number} sortDirection - Sort direction where -1 means descending.
 * @returns {string} Sort string accepted by API (e.g. "name", "-name", or "" for no sort).
 */
export const buildRunbookTemplatesSortQuery = (sortBy, sortDirection) => {
    if (!sortBy) {
        return '-priority'
    }

    const sortPrefix = sortDirection === -1 ? '-' : ''
    return `${sortPrefix}${sortBy}`
}

/**
 * Fetches one templates page directly, without mutating Redux templates state.
 * @param {{
 *  pageNumber: number,
 *  currentSort: string,
 *  normalizedSearchQuery: string,
 *  apiFilters: object
 * }} params
 * @returns {Promise<Array<object>>}
 */
export const fetchTemplatePageDirectly = async ({
    pageNumber,
    currentSort,
    normalizedSearchQuery,
    apiFilters,
}) => {
    const query = buildQueryString({
        page: pageNumber,
        per_page: ITEMS_PER_PAGE,
        sort: currentSort,
        search: normalizedSearchQuery,
        ...apiFilters,
    })

    const response = await fetch.get(`/api/runbooks/templates/${query}`)
    return response?.data?.results || []
}

/**
 * Shared priority swap handler used by up/down actions.
 * @param {object} template - Template to swap
 * @param {object} context - Swap context containing state, callbacks, and filters
 * @returns {Promise<void>}
 */
export const handleSwapTemplatePriority = async (template, context) => {
    const {
        direction,
        page,
        totalPages,
        visibleTemplates,
        isPrioritySwapDisabled,
        isSwappingPriority,
        currentSort,
        normalizedSearchQuery,
        apiFilters,
        swapRunbookTemplatePriority,
        getRunbookTemplatesFromServer,
        changeRunbookTemplatesPage,
        setVisibleTemplates,
        setIsSwappingPriority,
        showToast,
        dangerIntent,
    } = context

    if (isPrioritySwapDisabled || isSwappingPriority || !template?.id) {
        return
    }

    const currentIndex = visibleTemplates.findIndex(
        (currentTemplate) => currentTemplate.id === template.id
    )

    if (currentIndex < 0) {
        return
    }

    const sourceTemplate = visibleTemplates[currentIndex]
    let targetTemplate = null
    let nextTemplates = [...visibleTemplates]
    let destinationPage = null
    const templatesQueryParams = {
        per_page: ITEMS_PER_PAGE,
        sort: currentSort,
        search: normalizedSearchQuery,
        ...apiFilters,
    }

    const fetchTemplatesPage = (pageNumber) =>
        fetchTemplatePageDirectly({
            pageNumber,
            currentSort,
            normalizedSearchQuery,
            apiFilters,
        })

    const refreshTemplatesPage = (pageNumber) =>
        getRunbookTemplatesFromServer({
            page: pageNumber,
            ...templatesQueryParams,
        })

    if (direction === SWAP_PRIORITY_UP) {
        const targetIndex = currentIndex - 1

        if (targetIndex >= 0) {
            targetTemplate = visibleTemplates[targetIndex]
            ;[nextTemplates[currentIndex], nextTemplates[targetIndex]] = [
                nextTemplates[targetIndex],
                nextTemplates[currentIndex],
            ]
        } else if (page > 1) {
            const previousPageTemplates = await fetchTemplatesPage(page - 1)
            targetTemplate =
                previousPageTemplates[previousPageTemplates.length - 1]
            destinationPage = page - 1

            if (targetTemplate) {
                nextTemplates[0] = targetTemplate
            }
        }
    } else {
        const targetIndex = currentIndex + 1

        if (targetIndex < visibleTemplates.length) {
            targetTemplate = visibleTemplates[targetIndex]
            ;[nextTemplates[currentIndex], nextTemplates[targetIndex]] = [
                nextTemplates[targetIndex],
                nextTemplates[currentIndex],
            ]
        } else if (page < totalPages) {
            const nextPageTemplates = await fetchTemplatesPage(page + 1)
            targetTemplate = nextPageTemplates[0]
            destinationPage = page + 1

            if (targetTemplate) {
                nextTemplates[currentIndex] = targetTemplate
            }
        }
    }

    if (!sourceTemplate?.id || !targetTemplate?.id) {
        return
    }

    const previousTemplates = visibleTemplates

    setVisibleTemplates(nextTemplates)
    setIsSwappingPriority(true)

    try {
        await swapRunbookTemplatePriority(sourceTemplate.id, targetTemplate.id)

        if (destinationPage) {
            changeRunbookTemplatesPage(destinationPage)
            await refreshTemplatesPage(destinationPage)
            return
        }

        await refreshTemplatesPage(page)
    } catch (_error) {
        setVisibleTemplates(previousTemplates)
        showToast(
            'Failed to update template priority. Please try again.',
            dangerIntent
        )
    } finally {
        setIsSwappingPriority(false)
    }
}

/**
 * Swap selected template up in priority ordering.
 * @param {object} template
 * @param {object} context
 * @returns {Promise<void>}
 */
export const handleSwapPriorityUp = (template, context) =>
    handleSwapTemplatePriority(template, {
        ...context,
        direction: SWAP_PRIORITY_UP,
    })

/**
 * Swap selected template down in priority ordering.
 * @param {object} template
 * @param {object} context
 * @returns {Promise<void>}
 */
export const handleSwapPriorityDown = (template, context) =>
    handleSwapTemplatePriority(template, {
        ...context,
        direction: SWAP_PRIORITY_DOWN,
    })
